import { gridSnapStep, NO_SNAP, type SnapSettings } from '@flobi/artwork-core';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * What the person looking at the file has switched on.
 *
 * None of this is the document. A guide is document data — two people lining things up
 * are lining them up against the same lines — but whether you are SHOWN the guides, and
 * whether a drag is pulled onto them, is about the person and not the file. Somebody who
 * works with the grid on wants it on in the next file too, and somebody who turned the
 * rulers off did not turn them off for their colleague.
 *
 * So these live in the browser, not in the document, and not per document either: they
 * are how this person works, the same way Illustrator keeps them for the app. What IS
 * per document — how far apart the grid's lines are — sits in the document's own
 * settings beside its unit, because a box net drawn on a 5 mm grid is on a 5 mm grid on
 * anybody's machine.
 */
export interface ViewSettings {
  /** The two strips of numbers down the top and left edge, which guides are dragged from. */
  readonly rulers: boolean;
  readonly grid: boolean;
  /** The checkerboard, so what is transparent looks transparent rather than white. */
  readonly transparency: boolean;
  readonly guides: boolean;
  /** Locked guides stay where they are: they are there to be aimed at, not moved. */
  readonly lockGuides: boolean;
  /** Line things up with other things, and draw the line that says why. */
  readonly smart: boolean;
  /** Land exactly on a guide, or on another path's anchor. */
  readonly snapPoint: boolean;
  readonly snapGrid: boolean;
  readonly snapPixel: boolean;
}

export type ViewKey = keyof ViewSettings;

/**
 * Smart guides on and snap to point on, which is how Illustrator opens — they are the
 * two that make a drawing tidy without anybody asking for them. The rulers and the grid
 * are off, because they are furniture: useful when you want them, in the way when you
 * do not, and a blank canvas should be blank.
 */
export const VIEW_DEFAULTS: ViewSettings = {
  rulers: false,
  grid: false,
  transparency: false,
  guides: true,
  lockGuides: false,
  smart: true,
  snapPoint: true,
  snapGrid: false,
  snapPixel: false,
};

const STORE = 'artwork:view';

function readStored(): ViewSettings {
  try {
    const raw = window.localStorage.getItem(STORE);
    if (!raw) return VIEW_DEFAULTS;
    const saved = JSON.parse(raw) as Partial<Record<ViewKey, unknown>>;
    const out = { ...VIEW_DEFAULTS } as Record<ViewKey, boolean>;
    // Field by field, because a setting added after somebody last saved must arrive at
    // its default rather than as undefined — the same rule the document's own fields
    // follow, for the same reason.
    for (const key of Object.keys(VIEW_DEFAULTS) as ViewKey[]) {
      if (typeof saved[key] === 'boolean') out[key] = saved[key];
    }
    return out;
  } catch {
    // A browser with storage switched off is a browser that gets the defaults, not a
    // dead editor.
    return VIEW_DEFAULTS;
  }
}

/** Told to every reader in this page when one of them changes a switch (the browser's own event reaches other tabs only). */
const CHANGED = 'artwork:view-changed';

function remember(next: ViewSettings) {
  try {
    window.localStorage.setItem(STORE, JSON.stringify(next));
  } catch {
    // Not being able to remember it is no reason not to do it.
  }
  window.dispatchEvent(new CustomEvent<ViewSettings>(CHANGED, { detail: next }));
}

export function useViewSettings(): {
  view: ViewSettings;
  toggle: (key: ViewKey) => void;
  set: (key: ViewKey, on: boolean) => void;
} {
  const [view, setViewState] = useState<ViewSettings>(VIEW_DEFAULTS);
  // What is switched on right now, so two quick changes build on each other even where storage is refused.
  const latest = useRef<ViewSettings>(VIEW_DEFAULTS);
  const setView = useCallback((next: ViewSettings) => {
    latest.current = next;
    setViewState(next);
  }, []);

  // Read after mounting rather than during it: the first render has to match what the
  // server would have produced, and reaching for localStorage while rendering does not.
  useEffect(() => setView(readStored()), [setView]);

  // The Settings window changes the same switches from beside the editor: follow it.
  useEffect(() => {
    // What was said in this page, as said; what another tab stored, as stored.
    const followHere = (event: Event) => setView((event as CustomEvent<ViewSettings>).detail);
    const followStore = () => setView(readStored());
    window.addEventListener(CHANGED, followHere);
    window.addEventListener('storage', followStore);
    return () => {
      window.removeEventListener(CHANGED, followHere);
      window.removeEventListener('storage', followStore);
    };
  }, [setView]);

  const set = useCallback((key: ViewKey, on: boolean) => {
    const next = { ...latest.current, [key]: on };
    setView(next);
    remember(next);
  }, [setView]);

  const toggle = useCallback((key: ViewKey) => {
    const next = { ...latest.current, [key]: !latest.current[key] };
    setView(next);
    remember(next);
  }, [setView]);

  return { view, toggle, set };
}

/**
 * What a drag is allowed to be pulled by, out of what is switched on, how far apart the
 * document's grid is, and how closely the person is looking at it.
 *
 * Said in one place so the four toggles mean the same thing to every tool.
 *
 * The zoom is in here because of the grid, and only because of the grid. It is the one
 * rule measured in document units while the rest are measured in screen pixels, so it
 * is the one rule whose feel would otherwise fall apart as the page is zoomed out —
 * which is exactly what "snapping to grid is very very weak" turned out to mean. The
 * subdivision is used while it is worth aiming at and a whole multiple of it past that;
 * see `gridSnapStep`.
 */
export function snapSettingsOf(view: ViewSettings, grid: { step: number; divisions: number }, unitsPerPixel: number): SnapSettings {
  if (!view.smart && !view.snapPoint && !view.snapGrid && !view.snapPixel) return NO_SNAP;
  return {
    smart: view.smart,
    toGuides: view.snapPoint,
    toGrid: view.snapGrid,
    toPixel: view.snapPixel,
    gridStep: view.snapGrid ? gridSnapStep(grid.step, grid.divisions, unitsPerPixel) : 0,
  };
}
